import React from "react";
import {
	Box,
	Button,
	Dialog,
	Typography,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	MenuItem,
	Stack,
	TextField,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {palette} from "../../../../../theme/common";

const PRESET_UNITS = new Set([
	"шт",
	"пара",
	"комплект",
	"уп.",
	"час",
	"нормо-час",
	"км",
	"кг",
	"л",
	"м",
	"м2",
	"м3",
]);

const emptyForm = () => ({
	description: "",
	quantity: "",
	unit: "",
	unitCustom: "",
	cost: "",
});

const pictureUrl = (pic) => {
	if (pic?.url) {
		return pic.url;
	}
	if (pic?.name) {
		return [process.env.REACT_APP_HOST_API, "/static/service/", pic.name].join("");
	}
	return "";
};

class DialogAdditionalWork extends React.PureComponent {
	constructor(props) {
		super(props);
		this.initialPictureIdsRef = [];
		this.fileInputRef = React.createRef();
		this.state = {
			isOpen: false,
			editingId: null,
			form: emptyForm(),
			descriptionError: false,
			pendingFiles: [],
			existingPictures: [],
			removedPictureIds: [],
		};
	}

	open = () => {
		this.initialPictureIdsRef = [];
		this.setState({
			isOpen: true,
			editingId: null,
			form: emptyForm(),
			descriptionError: false,
			pendingFiles: [],
			existingPictures: [],
			removedPictureIds: [],
		});
	};

	openEdit = (work) => {
		if (!work) {
			return;
		}
		const u = String(work.unit || "").trim();
		const usePreset = PRESET_UNITS.has(u);
		this.initialPictureIdsRef = (work.pictures || []).map((p) => p.id).filter((id) => id != null);
		this.setState({
			isOpen: true,
			editingId: work.id,
			form: {
				description: work.description || "",
				quantity: work.quantity != null ? String(work.quantity) : "",
				unit: usePreset ? u : (u ? "другое" : ""),
				unitCustom: usePreset ? "" : u,
				cost: work.cost != null ? String(work.cost) : "",
			},
			descriptionError: false,
			pendingFiles: [],
			existingPictures: [...(work.pictures || [])],
			removedPictureIds: [],
		});
	};

	close = () => {
		this.state.pendingFiles.forEach((item) => {
			if (item.previewUrl) {
				URL.revokeObjectURL(item.previewUrl);
			}
		});
		this.initialPictureIdsRef = [];
		this.setState({
			isOpen: false,
			editingId: null,
			form: emptyForm(),
			descriptionError: false,
			pendingFiles: [],
			existingPictures: [],
			removedPictureIds: [],
		});
	};

	handleChange = (field) => (e) => {
		this.setState((prev) => ({
			form: {...prev.form, [field]: e.target.value},
			...(field === "description" ? {descriptionError: false} : {}),
		}));
	};

	handleUnitChange = (e) => {
		const value = e.target.value;
		this.setState((prev) => ({
			form: {
				...prev.form,
				unit: value,
				unitCustom: value === "другое" ? prev.form.unitCustom : "",
			},
		}));
	};

	handlePickFiles = (e) => {
		const list = Array.from(e.target.files || []);
		const added = list.map((file) => ({
			file,
			previewUrl: URL.createObjectURL(file),
		}));
		this.setState((prev) => ({
			pendingFiles: [...prev.pendingFiles, ...added],
		}));
		e.target.value = "";
	};

	removePendingFile = (index) => {
		this.setState((prev) => {
			const next = [...prev.pendingFiles];
			const [removed] = next.splice(index, 1);
			if (removed?.previewUrl) {
				URL.revokeObjectURL(removed.previewUrl);
			}
			return {pendingFiles: next};
		});
	};

	removeExistingPicture = (pic) => {
		const id = pic?.id;
		if (id == null) {
			return;
		}
		this.setState((prev) => ({
			existingPictures: prev.existingPictures.filter((p) => p.id !== id),
			removedPictureIds: [...prev.removedPictureIds, id],
		}));
	};

	submit = async () => {
		const {form, editingId, pendingFiles, removedPictureIds} = this.state;
		const {onSubmit, onSyncPictures} = this.props;
		if (!onSubmit) {
			return;
		}
		const body = {
			description: form.description.trim(),
			quantity: form.quantity,
			unit: (form.unit === "другое" ? String(form.unitCustom || "") : form.unit).trim(),
			cost: form.cost,
		};
		if (!body.description) {
			this.setState({descriptionError: true});
			return;
		}
		const meta = editingId != null ? {additionalWorkId: editingId} : null;
		const result = await onSubmit(body, meta);
		if (result === null) {
			return;
		}

		const aw = result?.additionalWork ?? result;
		const additionalWorkId = aw?.id ?? editingId;
		const rawPending = pendingFiles.map((p) => p.file);

		if (additionalWorkId && onSyncPictures) {
			await onSyncPictures({
				additionalWorkId,
				isEditMode: editingId != null,
				pendingFiles: rawPending,
				initialPictureIds: [...this.initialPictureIdsRef],
				removedPictureIds: rawPending.length > 0 ? [] : [...removedPictureIds],
			});
		}

		this.close();
	};

	render() {
		const {isOpen, form, descriptionError, editingId, pendingFiles, existingPictures} = this.state;
		const isEdit = editingId != null;

		const fieldHelperSx = {fontSize: 12, marginTop: "4px", lineHeight: 1.35};

		return (
			<Dialog
				open={isOpen}
				onClose={this.close}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					elevation: 8,
					sx: {
						borderRadius: 2,
						overflow: "hidden",
						boxShadow: "0 12px 40px rgba(21, 36, 53, 0.12)",
					},
				}}
			>
				<DialogTitle
					sx={{
						bgcolor: palette.primary.main,
						py: 2.5,
						px: 3,
					}}
				>
					<Typography
						variant="h3"
						sx={{
							textAlign: "left",
							color: "white",
							fontWeight: 600,
							letterSpacing: "-0.02em",
							fontSize: {xs: "1.15rem", sm: "1.25rem"},
						}}
					>
						{isEdit ? "Редактирование доп. работы" : "Дополнительная работа"}
					</Typography>
				</DialogTitle>
				<DialogContent
					sx={{
						maxHeight: "calc(100vh - 180px)",
						overflowY: "auto",
						px: {xs: 2, sm: 3},
						pt: 3,
						pb: 1,
						bgcolor: "#FAFBFC",
					}}
				>
					<Grid container direction="column" spacing={2.5} sx={{mt: 0}}>
						<Grid item xs={12}>
							<TextField
								label="Описание"
								placeholder="Напр. Замена масла"
								fullWidth
								required
								error={descriptionError}
								size="small"
								multiline
								minRows={2}
								helperText={
									descriptionError
										? "Заполните описание"
										: "Кратко опишите дополнительную работу"
								}
								FormHelperTextProps={{
									sx: fieldHelperSx,
								}}
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: 1.5,
										bgcolor: "#fff",
									},
								}}
								value={form.description}
								onChange={this.handleChange("description")}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Количество"
								placeholder="Напр. 2"
								fullWidth
								size="small"
								type="number"
								helperText="Сколько единиц нужно выполнить"
								FormHelperTextProps={{
									sx: fieldHelperSx,
								}}
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: 1.5,
										bgcolor: "#fff",
									},
								}}
								value={form.quantity}
								onChange={this.handleChange("quantity")}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Единица"
								fullWidth
								size="small"
								select
								value={form.unit}
								helperText="Единица измерения"
								FormHelperTextProps={{
									sx: fieldHelperSx,
								}}
								onChange={this.handleUnitChange}
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: 1.5,
										bgcolor: "#fff",
									},
									"& .MuiInputLabel-root": {color: palette.blackLight},
									"& .MuiInputBase-input": {color: palette.blackLight},
									"& .MuiSelect-select": {
										color: palette.blackLight,
										whiteSpace: "nowrap",
										overflow: "visible",
										textOverflow: "clip",
									},
									"& .MuiSvgIcon-root": {color: palette.blackLight},
								}}
								SelectProps={{
									MenuProps: {
										PaperProps: {
											sx: {
												bgcolor: "white",
												"& .MuiMenuItem-root": {
													color: palette.blackLight,
													whiteSpace: "nowrap",
													overflow: "visible",
													textOverflow: "clip",
												},
												"& .MuiList-root": {color: palette.blackLight},
											},
										},
									},
								}}
							>
								<MenuItem value="шт">шт</MenuItem>
								<MenuItem value="пара">пара</MenuItem>
								<MenuItem value="комплект">комплект</MenuItem>
								<MenuItem value="уп.">уп.</MenuItem>
								<MenuItem value="час">час</MenuItem>
								<MenuItem value="нормо-час">нормо-час</MenuItem>
								<MenuItem value="км">км</MenuItem>
								<MenuItem value="кг">кг</MenuItem>
								<MenuItem value="л">л</MenuItem>
								<MenuItem value="м">м</MenuItem>
								<MenuItem value="м2">м2</MenuItem>
								<MenuItem value="м3">м3</MenuItem>
								<MenuItem value="другое">другое</MenuItem>
							</TextField>

							{form.unit === "другое" && (
								<TextField
									label="Другая единица"
									fullWidth
									size="small"
									placeholder="Напр. рулон"
									value={form.unitCustom}
									helperText="Укажите свою единицу"
									FormHelperTextProps={{
										sx: fieldHelperSx,
									}}
									sx={{
										mt: 1,
										"& .MuiOutlinedInput-root": {
											borderRadius: 1.5,
											bgcolor: "#fff",
										},
									}}
									onChange={this.handleChange("unitCustom")}
								/>
							)}
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Стоимость"
								fullWidth
								size="small"
								type="number"
								helperText="Стоимость доп. работы"
								FormHelperTextProps={{
									sx: fieldHelperSx,
								}}
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: 1.5,
										bgcolor: "#fff",
									},
								}}
								value={form.cost}
								onChange={this.handleChange("cost")}
							/>
						</Grid>
						<Grid item xs={12}>
							<Box
								sx={{
									borderRadius: 2,
									p: 2,
									bgcolor: "#fff",
									border: "1px solid rgba(21, 36, 53, 0.08)",
									boxShadow: "0 1px 2px rgba(21, 36, 53, 0.04)",
								}}
							>
								<Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.5}}>
									<ImageOutlinedIcon sx={{fontSize: 22, color: "rgba(21, 36, 53, 0.45)"}}/>
									<Typography sx={{fontSize: 14, fontWeight: 600, color: "#152435", letterSpacing: "-0.02em"}}>
										Фотографии
									</Typography>
									<Typography component="span" sx={{fontSize: 12, color: "rgba(21, 36, 53, 0.45)"}}>
										· по желанию
									</Typography>
								</Stack>
								<input
									ref={this.fileInputRef}
									type="file"
									accept="image/*"
									multiple
									hidden
									onChange={this.handlePickFiles}
								/>
								<Button
									variant="outlined"
									size="medium"
									startIcon={<AddPhotoAlternateOutlinedIcon/>}
									onClick={() => this.fileInputRef.current?.click()}
									sx={{
										textTransform: "none",
										fontWeight: 600,
										borderRadius: 1.5,
										borderStyle: "dashed",
										borderColor: "rgba(21, 36, 53, 0.2)",
										color: palette.blackLight,
										py: 1,
										px: 2,
										"&:hover": {
											borderStyle: "dashed",
											borderColor: palette.primary.main,
											bgcolor: "rgba(255, 63, 63, 0.04)",
										},
									}}
								>
									Добавить фото
								</Button>
								<Typography variant="caption" display="block" sx={{mt: 1.25, color: "rgba(21, 36, 53, 0.5)", lineHeight: 1.45}}>
									{isEdit
										? "При замене снимков старые будут удалены, затем загрузятся новые."
										: "После сохранения работы файлы будут прикреплены к записи."}
								</Typography>

								{(existingPictures.length > 0 || pendingFiles.length > 0) && (
									<Grid container spacing={1.25} sx={{mt: 1.5}}>
										{existingPictures.map((pic) => (
											<Grid item key={`ex-${pic.id}`}>
												<Box
													sx={{
														position: "relative",
														width: 92,
														height: 92,
														borderRadius: 1.5,
														overflow: "hidden",
														border: "1px solid rgba(21, 36, 53, 0.08)",
														boxShadow: "0 2px 8px rgba(21, 36, 53, 0.06)",
													}}
												>
													<Box
														component="img"
														src={pictureUrl(pic)}
														alt=""
														sx={{width: "100%", height: "100%", objectFit: "cover"}}
													/>
													<IconButton
														size="small"
														sx={{
															position: "absolute",
															top: 4,
															right: 4,
															bgcolor: "rgba(255,255,255,0.95)",
															boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
															p: 0.35,
															"&:hover": {bgcolor: "#fff"},
														}}
														onClick={() => this.removeExistingPicture(pic)}
														aria-label="Убрать фото"
													>
														<DeleteOutlineIcon fontSize="small" sx={{color: "#647083"}}/>
													</IconButton>
												</Box>
											</Grid>
										))}
										{pendingFiles.map((item, index) => (
											<Grid item key={`new-${item.file.name}-${item.file.size}-${index}`}>
												<Box
													sx={{
														position: "relative",
														width: 92,
														height: 92,
														borderRadius: 1.5,
														overflow: "hidden",
														border: "1px dashed rgba(21, 36, 53, 0.2)",
														boxShadow: "0 1px 4px rgba(21, 36, 53, 0.05)",
													}}
												>
													<Box
														component="img"
														src={item.previewUrl}
														alt=""
														sx={{width: "100%", height: "100%", objectFit: "cover"}}
													/>
													<IconButton
														size="small"
														sx={{
															position: "absolute",
															top: 4,
															right: 4,
															bgcolor: "rgba(255,255,255,0.95)",
															boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
															p: 0.35,
														}}
														onClick={() => this.removePendingFile(index)}
														aria-label="Убрать"
													>
														<DeleteOutlineIcon fontSize="small" sx={{color: "#647083"}}/>
													</IconButton>
												</Box>
											</Grid>
										))}
									</Grid>
								)}
							</Box>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions
					sx={{
						borderTop: "1px solid rgba(21, 36, 53, 0.08)",
						bgcolor: "#fff",
						px: 3,
						py: 2,
						gap: 1,
						justifyContent: "flex-end",
					}}
				>
					<Button
						variant="outlined"
						onClick={this.close}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							borderRadius: 1.5,
							minWidth: 100,
							borderColor: "rgba(21, 36, 53, 0.2)",
							color: "#647083",
							"&:hover": {
								borderColor: "rgba(21, 36, 53, 0.35)",
								bgcolor: "rgba(21, 36, 53, 0.04)",
							},
						}}
					>
						Отмена
					</Button>
					<Button
						variant="contained"
						onClick={this.submit}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							borderRadius: 1.5,
							minWidth: 120,
							boxShadow: "none",
							"&:hover": {boxShadow: "0 4px 12px rgba(255, 63, 63, 0.35)"},
						}}
					>
						Сохранить
					</Button>
				</DialogActions>
			</Dialog>
		);
	}
}

export default DialogAdditionalWork;
